import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../../components/Footer';
import Header from '../../../components/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton';
import MainURL from '../../../MainURL';
import PhoneNumberInput from '../../../components/PhoneNumberInput';
import {
  APPLICATION_DEPOSIT,
  findServerPlan,
  OPTIONAL_APPS,
  PLAN_TIERS,
  computeMonthlyFee,
  findPlanTier,
  formatManwon,
  type OptionalApp,
} from '../../price/pricingData';
import { VerifyCodePanel, VerifySendButton, readApiMessage, useCodeVerification } from './useCodeVerification';
import './Inquiry.scss';

const COLOR_GROUPS: { name: string; colors: string[] }[] = [
  { name: '레드', colors: ['#fff5f5', '#fed7d7', '#fc8181', '#f56565', '#e53e3e', '#c53030', '#822727'] },
  { name: '오렌지', colors: ['#fffaf0', '#feebc8', '#fbd38d', '#f6ad55', '#ed8936', '#dd6b20', '#9c4221'] },
  { name: '옐로우', colors: ['#fffff0', '#fefcbf', '#faf089', '#f6e05e', '#ecc94b', '#d69e2e', '#975a16'] },
  { name: '그린', colors: ['#f0fff4', '#c6f6d5', '#9ae6b4', '#68d391', '#48bb78', '#38a169', '#276749'] },
  { name: '블루', colors: ['#ebf8ff', '#bee3f8', '#90cdf4', '#63b3ed', '#4299e1', '#3182ce', '#2c5282'] },
  { name: '네이비', colors: ['#eef2f9', '#d6e0f0', '#aec2e0', '#7fa0cc', '#4f77ab', '#26437a', '#0d2b5e'] },
  { name: '인디고', colors: ['#ebf4ff', '#c3dafe', '#a3bffa', '#7f9cf5', '#667eea', '#5a67d8', '#434190'] },
  { name: '퍼플', colors: ['#faf5ff', '#e9d8fd', '#d6bcfa', '#b794f4', '#9f7aea', '#805ad5', '#553c9a'] },
  { name: '핑크', colors: ['#fff5f7', '#fed7e2', '#fbb6ce', '#f687b3', '#ed64a6', '#d53f8c', '#97266d'] },
  { name: '그레이', colors: ['#f7fafc', '#edf2f7', '#e2e8f0', '#cbd5e0', '#a0aec0', '#718096', '#2d3748'] },
];

const HEX_COLOR_RE = /^#([0-9a-fA-F]{6})$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_RE = /^01[016789]\d{7,8}$/;
const CERT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_TEST_DEVICES = 3;
// 연락처 휴대폰 인증(바로빌 카카오 알림톡). 서버 inquiry.js의 PHONE_VERIFICATION_REQUIRED와 짝.
const PHONE_VERIFY_ENABLED = true;
type TestPlatform = 'android' | 'ios';
const TEST_PLATFORMS: { key: TestPlatform; label: string; desc: string }[] = [
  { key: 'android', label: '안드로이드', desc: '안드로이드 휴대폰으로 받아봅니다' },
  { key: 'ios', label: '아이폰', desc: 'Apple 계정 이메일로 테스트 초대를 받습니다' },
];
type DeveloperAccountMethod = 'self' | 'shared' | 'agency';
const DEVELOPER_ACCOUNT_METHODS: { key: DeveloperAccountMethod; label: string; desc: string }[] = [
  { key: 'self', label: '직접 등록', desc: '교회에서 구글 플레이·애플 개발자 계정을 직접 등록합니다' },
  {
    key: 'shared',
    label: '계정 공유 후 등록 대행',
    desc: '구글 ID·비밀번호, 애플 ID·비밀번호를 공유해 주시면 개발자 계정 등록을 대신 진행합니다',
  },
  { key: 'agency', label: '모두 대행', desc: '계정 준비부터 개발자 계정 등록까지 모두 맡겨 주세요' },
];

const STEPS = [
  {
    num: '01',
    day: 'Step 1',
    title: '앱제작신청',
    desc: '성도 규모와 함께 사용할 앱을 선택해 신청서를 제출합니다.',
  },
  {
    num: '02',
    day: 'Step 2',
    title: '기본요금 입금',
    desc: '신청 확정 및 MVP 제작 착수를 위한 기본요금을 결제합니다.',
  },
  {
    num: '03',
    day: 'Step 3',
    title: 'MVP제작 · 시안확인',
    desc: '제작된 앱 MVP와 디자인 시안을 확인하고 승인합니다.',
  },
  {
    num: '04',
    day: 'Step 4',
    title: '계약서 작성 · 서명',
    desc: '선택한 앱과 월 요금을 확인하고 계약서에 전자서명합니다.',
  },
  {
    num: '05',
    day: 'Step 5',
    title: '최종결제',
    desc: '월 요금 정기결제를 등록하면 앱이 오픈됩니다.',
  },
];

const INITIAL_FORM = {
  church: '',
  name: '',
  email: '',
  adminEmail: '',
  phone: '',
  size: '',
  message: '',
  concept: '#0d2b5e',
  testPlatforms: [] as TestPlatform[],
  testAppleEmails: [''],
  developerAccountMethod: '' as DeveloperAccountMethod | '',
  agree: true,
};

export default function Inquiry() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [apps, setApps] = useState<OptionalApp[]>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPreview, setCertPreview] = useState<string | null>(null);

  const [toastOn, setToastOn] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const nodes = document.querySelectorAll('.inquiry-page .reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    nodes.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const scrollToForm = () => {
    document.getElementById('form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const selectedTier = findPlanTier(form.size);
  const serverPlan = findServerPlan(form.size);
  const estimatedMonthly = computeMonthlyFee(form.size, apps);

  const toggleApp = (value: OptionalApp) => {
    setApps((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const toggleTestPlatform = (value: TestPlatform) => {
    setForm((prev) => ({
      ...prev,
      testPlatforms: prev.testPlatforms.includes(value)
        ? prev.testPlatforms.filter((item) => item !== value)
        : [...prev.testPlatforms, value],
    }));
  };
  const setAppleEmail = (index: number, value: string) => {
    setForm((prev) => ({ ...prev, testAppleEmails: prev.testAppleEmails.map((item, i) => (i === index ? value : item)) }));
  };
  const addAppleEmail = () => {
    setForm((prev) =>
      prev.testAppleEmails.length >= MAX_TEST_DEVICES ? prev : { ...prev, testAppleEmails: [...prev.testAppleEmails, ''] }
    );
  };
  const removeAppleEmail = (index: number) => {
    setForm((prev) => ({ ...prev, testAppleEmails: prev.testAppleEmails.filter((_, i) => i !== index) }));
  };

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setLogoFile(file);
    setLogoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleCertChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (file && !CERT_TYPES.includes(file.type)) {
      showToast('고유번호증은 jpg, png, webp 이미지나 PDF로 첨부해 주세요.');
      return;
    }
    if (file && file.size > 10 * 1024 * 1024) {
      showToast('고유번호증은 10MB 이하로 첨부해 주세요.');
      return;
    }
    setCertFile(file);
    setCertPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    });
  };

  const removeCert = () => {
    setCertFile(null);
    setCertPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      if (certPreview) URL.revokeObjectURL(certPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastOn(true);
    window.setTimeout(() => setToastOn(false), 3200);
  };

  const emailVerification = useCodeVerification(form.email, {
    path: 'email-verification',
    field: 'email',
    normalize: (value) => value.trim().toLowerCase(),
    isValid: (value) => EMAIL_RE.test(value),
    invalidMessage: '이메일 주소를 확인해 주세요.',
    sentMessage: (email) => `${email}로 인증번호를 보냈습니다. 10분 안에 입력해 주세요.`,
    showToast,
  });
  const phoneVerification = useCodeVerification(form.phone, {
    path: 'phone-verification',
    field: 'phone',
    normalize: (value) => value.replace(/\D/g, ''),
    isValid: (value) => MOBILE_RE.test(value),
    invalidMessage: '휴대폰 번호를 확인해 주세요.',
    sentMessage: () => '카카오톡 알림톡으로 인증번호를 보냈습니다. 10분 안에 입력해 주세요.',
    showToast,
    extraSendBody: { name: form.name.trim() },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = {
      church: !form.church.trim(),
      name: !form.name.trim(),
      email: !form.email.trim() || !emailVerification.isVerified,
      phone: !form.phone.trim() || (PHONE_VERIFY_ENABLED && !phoneVerification.isVerified),
      size: !form.size.trim(),
      developerAccountMethod: !form.developerAccountMethod,
    };
    setErrors(nextErrors);
    if (submitting) return;
    if (form.email.trim() && !emailVerification.isVerified) {
      showToast('이메일 인증을 완료해 주세요.');
      return;
    }
    if (PHONE_VERIFY_ENABLED && form.phone.trim() && !phoneVerification.isVerified) {
      showToast('휴대폰 번호 인증을 완료해 주세요.');
      return;
    }
    if (Object.values(nextErrors).some(Boolean)) {
      showToast(
        Object.entries(nextErrors).every(([key, bad]) => key === 'developerAccountMethod' || !bad)
          ? '개발자 계정 등록 방식을 선택해 주세요.'
          : '필수 입력 항목(*)을 모두 입력해 주세요.'
      );
      return;
    }
    const wantsIos = form.testPlatforms.includes('ios');
    const appleEmails = wantsIos ? form.testAppleEmails.map((v) => v.trim()).filter(Boolean) : [];
    if (wantsIos && appleEmails.length === 0) {
      setErrors((prev) => ({ ...prev, testAppleEmails: true }));
      showToast('아이폰으로 받아보시려면 Apple 계정 이메일을 입력해 주세요.');
      return;
    }
    if (appleEmails.some((v) => !EMAIL_RE.test(v))) {
      setErrors((prev) => ({ ...prev, testAppleEmails: true }));
      showToast('테스트용 아이폰의 Apple 계정 이메일을 확인해 주세요.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('church', form.church);
      payload.append('name', form.name);
      payload.append('email', form.email);
      payload.append('adminEmail', form.adminEmail.trim() || form.email);
      payload.append('phone', form.phone);
      payload.append('size', form.size);
      payload.append('message', form.message);
      payload.append('concept', form.concept);
      payload.append('apps', JSON.stringify(apps));
      payload.append('testPlatforms', JSON.stringify(form.testPlatforms));
      payload.append('testAppleEmails', JSON.stringify(appleEmails));
      payload.append('developerAccountMethod', form.developerAccountMethod);
      payload.append('emailVerificationToken', emailVerification.token);
      if (PHONE_VERIFY_ENABLED) payload.append('phoneVerificationToken', phoneVerification.token);
      if (logoFile) payload.append('logo', logoFile);
      if (certFile) payload.append('registrationCert', certFile);

      const res = await axios.post<{
        ok: boolean;
        message?: string;
        id?: number;
      }>(`${MainURL}/inquiry`, payload);

      if (res.data?.ok) {
        navigate('/inquiry-complete');
        return;
      } else {
        showToast(res.data?.message || '신청에 실패했습니다. 다시 시도해 주세요.');
      }
    } catch (err) {
      showToast(readApiMessage(err) || '신청에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inquiry-page">
      <Header />

      <section className="page-header">
        <div className="container reveal">
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>· Apply</span>
          </div>
          <div className="page-header-grid">
            <div>
              <h1 className="page-title">
                우리 교회 앱,
                <br />
                <span className="em">신청</span>부터 시작합니다.
              </h1>
              <p className="page-sub">
                성도 규모와 사용할 앱을 선택해 신청서를 남겨주시면, 기본요금 결제 후 MVP
                제작이 시작됩니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="process">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">How it works</span>
            <h2>
              신청부터 오픈까지, <span className="em">5단계</span>.
            </h2>
          </div>
          <div className="steps reveal">
            {STEPS.map((step) => (
              <div className="step" key={step.num}>
                <div className="step-dot" />
                <div className="step-num">{step.num}</div>
                <div className="step-day">{step.day}</div>
                <div className="step-title">{step.title}</div>
                <div className="step-desc">{step.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="form-section" id="form">
        <div className="container">
          <div className="form-solo">
            <form className="form-card reveal" onSubmit={handleSubmit} noValidate>
              <div className="form-card-title">
                앱제작 <span className="em">신청서</span>
              </div>

              <div className="field-grid">
                <div className="field">
                  <label>
                    교회명 <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    name="church"
                    placeholder="예) 새생명교회"
                    value={form.church}
                    onChange={(e) => setForm({ ...form, church: e.target.value })}
                    style={errors.church ? { borderBottomColor: '#c9302c' } : undefined}
                    required
                  />
                </div>
                <div className="field">
                  <label>
                    담당자 성함 <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="예) 김요한 목사"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    style={errors.name ? { borderBottomColor: '#c9302c' } : undefined}
                    required
                  />
                </div>
                <div className="field">
                  <label>
                    이메일 <span className="req">*</span>
                  </label>
                  <div className="email-verify-row">
                    <input
                      type="email"
                      name="email"
                      placeholder="you@church.kr"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      style={errors.email ? { borderBottomColor: '#c9302c' } : undefined}
                      readOnly={emailVerification.isVerified}
                      required
                    />
                    <VerifySendButton v={emailVerification} />
                  </div>
                  <VerifyCodePanel v={emailVerification} doneMessage="이메일 인증이 완료되었습니다." />
                </div>
                <div className="field">
                  <label>
                    연락처 <span className="req">*</span>
                  </label>
                  <div className="email-verify-row">
                    <PhoneNumberInput
                      value={form.phone}
                      onChange={(phone) => setForm({ ...form, phone })}
                      groupClassName="email-verify-phone"
                      style={errors.phone ? { borderBottomColor: '#c9302c' } : undefined}
                      disabled={PHONE_VERIFY_ENABLED && phoneVerification.isVerified}
                      required
                    />
                    {PHONE_VERIFY_ENABLED && <VerifySendButton v={phoneVerification} />}
                  </div>
                  {PHONE_VERIFY_ENABLED && (
                    <VerifyCodePanel v={phoneVerification} doneMessage="휴대폰 번호 인증이 완료되었습니다." />
                  )}
                </div>
                <div className="field full">
                  <label>관리자 로그인 이메일</label>
                  <input
                    type="email"
                    name="adminEmail"
                    placeholder={form.email.trim() ? `비워 두면 ${form.email.trim()}` : '비워 두면 위 이메일로 사용합니다'}
                    value={form.adminEmail}
                    onChange={(e) => setForm({ ...form, adminEmail: e.target.value })}
                  />
                  <p className="field-hint">
                    교회앱관리 · 교적관리 · 재정관리 페이지와 관리자 앱에 로그인할 아이디입니다. 비밀번호는 앱이 완성되고
                    최종 결제가 끝나면 담당자가 따로 알려 드립니다.
                  </p>
                </div>
                <div className="field full">
                  <label>
                    성도 규모 <span className="req">*</span>
                  </label>
                  <select
                    name="size"
                    value={form.size}
                    className={form.size ? 'filled' : ''}
                    onChange={(e) => setForm({ ...form, size: e.target.value })}
                    style={errors.size ? { borderBottomColor: '#c9302c' } : undefined}
                    required
                  >
                    <option value="">선택해주세요</option>
                    {PLAN_TIERS.map((item) => (
                      <option key={item.size}>{item.size}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-subtitle">
                사용할 앱 <small>Apps</small>
              </div>
              <div className="app-options">
                <div className="app-option app-option--base">
                  <span className="app-option__name">
                    성도용 앱 + PC 관리자 <em>기본</em>
                  </span>
                  <span className="app-option__desc">예배순서·교회소식·설교·갤러리·교회일정 등</span>
                  <span className="app-option__price">
                    {selectedTier ? `월 ${formatManwon(selectedTier.base)}` : '규모 선택 시 표시'}
                  </span>
                </div>
                {OPTIONAL_APPS.map((app) => (
                  <label
                    className={`app-option${apps.includes(app.key) ? ' active' : ''}`}
                    key={app.key}
                  >
                    <input
                      type="checkbox"
                      checked={apps.includes(app.key)}
                      onChange={() => toggleApp(app.key)}
                    />
                    <span className="app-option__name">
                      <span className="box" />
                      {app.label}
                    </span>
                    <span className="app-option__desc">{app.desc}</span>
                    <span className="app-option__price">
                      {selectedTier ? `+${formatManwon(selectedTier.apps[app.key])} / 월` : '규모 선택 시 표시'}
                    </span>
                  </label>
                ))}
              </div>

              <div className="form-subtitle">
                요금 <small>Price</small>
              </div>
              <div className="price-estimate">
                <span className="price-label">예상 월 요금</span>
                <span className="price-amount">
                  {estimatedMonthly != null
                    ? `${estimatedMonthly.toLocaleString()}원`
                    : '성도 규모를 선택해 주세요'}
                </span>
              </div>
              <div className="price-estimate price-deposit">
                <span className="price-label">신청 기본요금</span>
                <span className="price-amount">{APPLICATION_DEPOSIT.toLocaleString()}원</span>
              </div>
              {serverPlan && (
                <div className="server-build">
                  <div className="price-estimate price-deposit">
                    <span className="price-label">단독 서버 구축비 ({serverPlan.label})</span>
                    <span className="price-amount">{serverPlan.needed ? serverPlan.amount : '해당 없음'}</span>
                  </div>
                  {serverPlan.needed && (
                    <p className="server-build__note">시안 확인 후, 서버 구축비 결제를 해주셔야 작업이 진행됩니다.</p>
                  )}
                </div>
              )}
              <p className="field-hint">
                * 성도용 앱의 갤러리는 기본 10GB 용량이며, 필요 시 10GB당 1,000원이 추가됩니다.
                <br />* 구글, 애플 개발자 계정 비용은 별도입니다(약 월 11,000원).
                <br />* 기본요금 결제는 신청 접수 후 담당자 확인을 거쳐 별도로 안내드립니다.
              </p>

              <div className="upload-grid">
                <div className="upload-col">
                  <div className="form-subtitle">
                    교회로고 <small>Church Logo</small>
                  </div>
                  <div className="logo-upload">
                    {logoPreview ? (
                      <div className="logo-preview">
                        <img src={logoPreview} alt="교회로고 미리보기" />
                        <button type="button" className="logo-remove" onClick={removeLogo}>
                          삭제
                        </button>
                      </div>
                    ) : (
                      <label className="logo-dropzone">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={handleLogoChange}
                        />
                        <span>클릭해서 로고 이미지 첨부</span>
                        <small>JPG, PNG, WEBP, GIF · 최대 10MB</small>
                      </label>
                    )}
                  </div>
                </div>
                <div className="upload-col">
                  <div className="form-subtitle">
                    고유번호증 <small>Registration</small>
                  </div>
                  <div className="logo-upload">
                    {certFile ? (
                      <div className="logo-preview">
                        {certPreview ? (
                          <img src={certPreview} alt="고유번호증 미리보기" />
                        ) : (
                          <span className="cert-file-icon" aria-hidden="true">
                            PDF
                          </span>
                        )}
                        <span className="cert-file-name">{certFile.name}</span>
                        <button type="button" className="logo-remove" onClick={removeCert}>
                          삭제
                        </button>
                      </div>
                    ) : (
                      <label className="logo-dropzone">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          onChange={handleCertChange}
                        />
                        <span>클릭해서 고유번호증 첨부</span>
                        <small>JPG, PNG, WEBP, PDF · 최대 10MB</small>
                      </label>
                    )}
                  </div>
                  <p className="field-hint">교회 이름으로 앱을 등록할 때 필요한 서류입니다. 운영진만 확인합니다.</p>
                </div>
              </div>

              <div className="form-subtitle">
                테스트용(시안) 앱 받아볼 기기 <small>Test Devices</small>
              </div>
              <div className="app-options">
                {TEST_PLATFORMS.map((platform) => (
                  <label
                    className={`app-option${form.testPlatforms.includes(platform.key) ? ' active' : ''}`}
                    key={platform.key}
                  >
                    <input
                      type="checkbox"
                      checked={form.testPlatforms.includes(platform.key)}
                      onChange={() => toggleTestPlatform(platform.key)}
                    />
                    <span className="app-option__name">
                      <span className="box" />
                      {platform.label}
                    </span>
                    <span className="app-option__desc">{platform.desc}</span>
                  </label>
                ))}
              </div>
              {form.testPlatforms.includes('ios') && (
                <div className="field test-device-emails">
                  <label>아이폰 · Apple 계정 이메일 *</label>
                  {form.testAppleEmails.map((value, index) => (
                    <div className="test-device-row" key={`apple-${index}`}>
                      <input
                        type="email"
                        placeholder="appleid@icloud.com"
                        value={value}
                        onChange={(e) => setAppleEmail(index, e.target.value)}
                        style={errors.testAppleEmails ? { borderBottomColor: '#c9302c' } : undefined}
                      />
                      {form.testAppleEmails.length > 1 && (
                        <button
                          type="button"
                          className="test-device-remove"
                          onClick={() => removeAppleEmail(index)}
                          aria-label="이 이메일 삭제"
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  ))}
                  {form.testAppleEmails.length < MAX_TEST_DEVICES && (
                    <button type="button" className="test-device-add" onClick={addAppleEmail}>
                      + 이메일 추가
                    </button>
                  )}
                </div>
              )}
              <p className="field-hint">
                제작한 앱(MVP)을 설치해 미리 확인해 보실 기기입니다. 구글·애플 각각 3개까지, 쓰시는 기기 쪽만 입력하셔도 됩니다. 아이폰은
                App Store에 로그인된 Apple 계정 이메일을 적어 주세요.
              </p>

              <div className="form-subtitle">
                개발자 계정 등록 방식 <span className="req">*</span> <small>Developer Account</small>
                {/* 새 탭으로 열어 작성 중인 신청서가 지워지지 않게 한다. */}
                <Link
                  to="/developer-account-guide"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dev-guide-link"
                >
                  개발자 계정 등록 안내 →
                </Link>
              </div>
              <div className={`app-options${errors.developerAccountMethod ? ' app-options--error' : ''}`}>
                {DEVELOPER_ACCOUNT_METHODS.map((method, index) => (
                  <label
                    className={`app-option${form.developerAccountMethod === method.key ? ' active' : ''}`}
                    key={method.key}
                  >
                    <input
                      type="radio"
                      name="developerAccountMethod"
                      checked={form.developerAccountMethod === method.key}
                      onChange={() => {
                        setForm({ ...form, developerAccountMethod: method.key });
                        setErrors((prev) => ({ ...prev, developerAccountMethod: false }));
                      }}
                    />
                    <span className="app-option__name">
                      <span className="box" />
                      {index + 1}. {method.label}
                    </span>
                    <span className="app-option__desc">{method.desc}</span>
                  </label>
                ))}
              </div>
              <p className="field-hint">
                앱스토어·플레이스토어에 교회 앱을 올리려면 교회 명의의 개발자 계정이 필요합니다.{' '}
                {form.developerAccountMethod === 'shared' &&
                  '계정 아이디·비밀번호는 신청서에 적지 마세요. 접수 후 담당자가 따로 연락드려 받습니다.'}
              </p>

              <div className="form-subtitle">
                메인 색상 <small>Main Color</small>
              </div>
              <div className="color-picker">
                <div className="color-preview" style={{ background: form.concept }} />
                <input
                  type="text"
                  className="color-hex-input"
                  value={form.concept}
                  onChange={(e) => setForm({ ...form, concept: e.target.value })}
                  placeholder="#0d2b5e"
                  maxLength={7}
                />
                <input
                  type="color"
                  className="color-native-input"
                  value={HEX_COLOR_RE.test(form.concept) ? form.concept : '#0d2b5e'}
                  onChange={(e) => setForm({ ...form, concept: e.target.value })}
                  aria-label="색상 직접 선택"
                />
              </div>
              <div className="color-palette">
                {COLOR_GROUPS.map((group) => (
                  <div className="color-group" key={group.name}>
                    <span className="color-group-label">{group.name}</span>
                    <div className="color-group-swatches">
                      {group.colors.map((hex) => (
                        <button
                          type="button"
                          key={hex}
                          className={`color-swatch${form.concept.toLowerCase() === hex ? ' active' : ''}`}
                          style={{ background: hex }}
                          aria-label={hex}
                          onClick={() => setForm({ ...form, concept: hex })}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-subtitle" style={{ marginTop: 36 }}>
                메시지 <small>Message</small>
              </div>
              <div className="field full">
                <textarea
                  name="message"
                  placeholder="궁금하신 점, 특별히 원하시는 방향을 자유롭게 남겨주세요."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>

              <div className="agree-row">
                <label className="chk agree-left">
                  <input
                    type="checkbox"
                    checked={form.agree}
                    onChange={(e) => setForm({ ...form, agree: e.target.checked })}
                  />
                  <span className="box" />
                  <span className="lbl">개인정보 수집·이용에 동의합니다.</span>
                  <a
                    href={`${MainURL}/uploads/personalinfo.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    상세 보기
                  </a>
                </label>
              </div>

              <div className="price-estimate price-deposit price-deposit--final">
                <span className="price-label">신청 기본요금</span>
                <span className="price-amount">{APPLICATION_DEPOSIT.toLocaleString()}원</span>
              </div>

              <div className="bank-account-notice">
                신청 후 아래 계좌로 입금해주세요
                <br />
                <span className="bank-account-notice__number">국민은행 676301-04-300117</span>
                <br />
                확인 후에 운영진에서 연락드리겠습니다.
              </div>

              <div className="submit-row">
                <span className="submit-note">* 표시는 필수 입력 항목입니다.</span>
                <button type="submit" className="btn-navy" disabled={submitting}>
                  {submitting ? '처리 중...' : <>신청하기 &nbsp;→</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="container final-cta-inner reveal">
          <h2>
            우리 교회의 이야기를,
            <br />
            <span className="em">들려주세요</span>.
          </h2>
          <p>지금 신청하시면, 담당자가 빠르게 다음 단계를 안내드립니다.</p>
          <a
            href="#form"
            className="btn-cream"
            onClick={(e) => {
              e.preventDefault();
              scrollToForm();
            }}
          >
            지금 신청하기 &nbsp;→
          </a>
        </div>
      </section>

      <section className="contact-bottom">
        <div className="container contact-bottom-grid reveal">
          <div className="contact-bottom-intro">
            <span className="eyebrow">Apply</span>
            <h2>
              궁금한 점은,
              <br />
              <span className="em">편하게</span> 연락 주세요.
            </h2>
            <p>
              이메일, 전화, 카카오톡 어떤 채널이든 좋습니다. 담당자가 직접 답변드립니다.
            </p>
          </div>
          <div className="contact-bottom-info">
            <ul className="inquiry-list">
              <li>
                <span className="label">Email</span>
                <span className="value">yeplat@naver.com</span>
                <span className="note">24시간 접수</span>
              </li>
              <li>
                <span className="label">Phone</span>
                <span className="value">1551-5948</span>
                <span className="note">평일 10:00 – 18:00</span>
              </li>
              <li>
                <span className="label">Kakao</span>
                <a
                  className="value"
                  href="http://pf.kakao.com/_xmpdXX"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  @yeplat
                </a>
                <span className="note">실시간 답변</span>
              </li>
            </ul>
            <div className="form-left-foot">
              · 주소
              <br />
              대구광역시 달성군 다사읍 달구벌대로 174길50. B202호
            </div>
          </div>
        </div>
      </section>

      <div className={`toast${toastOn ? ' on' : ''}`}>
        {toastMessage}
      </div>

      <ScrollToTopButton />
      <Footer />
    </div>
  );
}
